<template>
  <div class="tp">
    <section class="tp-selected">
      <div class="acq-label">{{ mode === 'assign' ? 'Zugeteilt' : 'Vorgemerkt' }} · {{ value.length }}</div>
      <div class="tp-chips">
        <span v-for="chip in selectedChips" :key="chip.id" :class="{ 'tp-chip--new': chip.isNew }" class="tp-chip">
          <span class="tp-chip__avatar ip-chip__avatar">{{ chip.initials }}</span>{{ chip.name }}
          <span v-if="chip.isNew" class="tag tag--sm tone-good">neu</span>
          <button :aria-label="`${chip.name} entfernen`" class="tp-chip__btn" title="Entfernen" type="button" @click="toggle(chip.id)">
            <v-icon x-small>mdi-close</v-icon>
          </button>
        </span>
        <span v-for="chip in removedChips" :key="`r-${chip.id}`" class="tp-chip tp-chip--removed">
          <span class="tp-chip__avatar ip-chip__avatar">{{ chip.initials }}</span><s>{{ chip.name }}</s>
          <button :aria-label="`${chip.name} wieder hinzufügen`" class="tp-chip__btn" title="Rückgängig" type="button" @click="toggle(chip.id)">
            <v-icon x-small>mdi-undo</v-icon>
          </button>
        </span>
        <span v-if="!selectedChips.length && !removedChips.length" class="acq-tiny">
          {{ mode === 'assign' ? 'Noch kein Standort zugeteilt.' : 'Noch kein Standort vorgemerkt.' }} Unten aus den Vorschlägen wählen.
        </span>
      </div>
      <div v-if="mode === 'assign' && teacher && teacher.isApplicant" class="acq-tiny tp-hint">
        <v-icon x-small>mdi-information-outline</v-icon>
        Noch Bewerber:in: Standorte werden meist erst vorgemerkt.
      </div>
    </section>

    <section class="tp-filters">
      <div class="soft-inputs">
        <v-text-field v-model="term" clearable color="info" dense hide-details outlined placeholder="Anderen Standort per Name suchen (ohne Umkreis)"
                      prepend-inner-icon="mdi-magnify"/>
      </div>
      <div class="tp-filter-row">
        <div v-if="origin && !searching" aria-label="Umkreis" class="pills" role="group">
          <button v-for="km in radiusSteps" :key="km" :aria-pressed="String(radius === km)" :class="{ 'pill--active': radius === km }" class="pill"
                  type="button" @click="radius = km">
            {{ km }} km
          </button>
        </div>
        <segmented-control v-model="who" :items="whoItems" label="Welche"/>
        <button v-if="teacherHasSlots" :aria-pressed="String(onlyMatching)" :class="{ 'pill--active': onlyMatching }" class="pill" type="button"
                @click="onlyMatching = !onlyMatching">
          <v-icon class="pill__icon" x-small>{{ onlyMatching ? 'mdi-check' : 'mdi-calendar-check-outline' }}</v-icon>
          nur mit passender Verfügbarkeit
        </button>
      </div>
      <div class="acq-tiny tp-hint">
        <v-icon x-small>{{ searching ? 'mdi-magnify' : 'mdi-sort-descending' }}</v-icon>
        {{ hint }}
      </div>
    </section>

    <div v-if="loading || searchLoading" class="tp-list">
      <v-skeleton-loader v-for="n in 4" :key="n" class="tp-row" type="list-item-avatar-three-line"/>
    </div>
    <div v-else-if="!shown.length" class="tp-empty">
      <div class="tp-empty__icon"><v-icon color="grey">mdi-domain-off</v-icon></div>
      <div class="font-weight-medium">{{ emptyTitle }}</div>
      <div class="acq-tiny">{{ emptyText }}</div>
    </div>
    <div v-else class="tp-list">
      <div v-for="(row, index) in shown" :key="row.id" :aria-pressed="String(isSelected(row.id))" :class="{ 'tp-row--selected': isSelected(row.id) }"
           class="tp-row" role="button" tabindex="0" @click="toggleRow(row)" @keydown.enter.prevent="toggleRow(row)" @keydown.space.prevent="toggleRow(row)">
        <div class="tp-avatar ip-avatar">
          {{ row.initials }}
          <span v-if="!searching && index < 3" class="tp-rank">{{ index + 1 }}</span>
        </div>
        <div class="tp-main">
          <div class="tp-name">
            <span class="text-truncate">{{ row.name }}</span>
            <span v-if="row.customId" class="ip-custom-id">{{ row.customId }}</span>
            <span v-if="isSelected(row.id) && !initialIds.includes(row.id)" class="tag tag--sm tone-good">neu</span>
            <span v-else-if="initialIds.includes(row.id)" class="tag tag--sm tone-info">{{ mode === 'assign' ? 'zugeteilt' : 'vorgemerkt' }}</span>
          </div>
          <div class="tp-meta">
            <span v-if="row.typeText" class="tag tag--sm tone-muted">{{ row.typeText }}</span>
            <v-icon x-small>mdi-map-marker-outline</v-icon>
            <span>{{ row.city || 'Ort unbekannt' }}</span>
            <template v-if="row.distanceKm !== null">
              <span>·</span><strong>{{ formatKm(row.distanceKm) }}</strong><span>Luftlinie</span>
            </template>
          </div>
          <div v-if="tagsOf(row).length" class="tp-tags">
            <span v-for="tag in tagsOf(row)" :key="tag.name" class="tag tag--sm">
              <span :style="{ background: tag.color || '#90A4AE' }" class="tp-subject__dot"/>{{ tag.name }}
            </span>
          </div>
          <div class="tp-tags">
            <span v-for="reason in row.reasons" :key="reason.text" :class="reason.tone" :title="reason.title" class="tag tag--sm">
              <v-icon class="tag__icon" x-small>{{ reason.icon }}</v-icon>{{ reason.text }}
            </span>
          </div>
        </div>
        <div class="tp-side">
          <span v-if="row.status" :class="row.status.tone" :title="row.status.title" class="tag tag--sm">
            <v-icon class="tag__icon" x-small>{{ row.status.icon }}</v-icon>{{ row.status.text }}
          </span>
          <div :title="dayTitle(row)" aria-label="Kurszeiten der Schule Mo bis Fr" class="tp-days">
            <span v-for="(state, day) in row.days" :key="day" :class="`tp-day--${state}`" class="tp-day">{{ workDays[day] }}</span>
          </div>
          <div class="tp-stats">
            <span v-if="row.institution.totalAmountPupils" title="Schüler:innen gesamt">
              <v-icon x-small>mdi-account-school-outline</v-icon>{{ row.institution.totalAmountPupils.toLocaleString('de-DE') }} Schüler:innen
            </span>
            <span v-if="teacherCount(row) !== null" title="Andere aktive Lehrkräfte, die hier zugeteilt sind">
              <v-icon x-small>mdi-human-male-board</v-icon>{{ teacherCount(row) }} {{ teacherCount(row) === 1 ? 'Lehrkraft' : 'Lehrkräfte' }}
            </span>
            <span v-if="courseCount(row) !== null" title="Aktive Kurse">
              <v-icon x-small>mdi-account-group-outline</v-icon>{{ courseCount(row) }} {{ courseCount(row) === 1 ? 'Kurs' : 'Kurse' }}
            </span>
          </div>
        </div>
        <div class="tp-action">
          <span :class="{ 'tp-check--on': isSelected(row.id) }" class="tp-check">
            <v-icon small>{{ isSelected(row.id) ? 'mdi-check' : 'mdi-plus' }}</v-icon>
          </span>
          <a :href="$router.resolve(`/institutions/${row.id}`).href" aria-label="Standort in neuem Tab öffnen" class="tp-open" target="_blank"
             title="Standort öffnen" @click.stop>
            <v-icon small>mdi-open-in-new</v-icon>
          </a>
        </div>
      </div>
      <div v-if="filtered.length > shown.length" class="text-center pt-2">
        <button class="cta cta--quiet" type="button" @click="limit += 20">Weitere {{ filtered.length - shown.length }} anzeigen</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {Types} from 'mongoose';
import SegmentedControl from '@/components/common/modern/segmentedControl.vue';
import {Employee} from '@/interfaces/employee.interface';
import {Institution} from '@/interfaces/institution.interface';
import {InstitutionerApi} from '@/classes/api/institutioner.api.class';
import {EmployeeHandlerAPI} from '@/classes/api/employee-handler.api.class';
import {CourseManagerAPI} from '@/classes/api/course-manager.api.class';
import {CourseStates} from '@/enums/CourseStates.enum';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {EmployeeFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/EmployeeFields';
import {
  courseSlots, formatKm, MAX_RADIUS_KM, PickerMode, RADIUS_STEPS, WORK_DAYS,
} from '@/components/institutions/institutionView/profileView/teacherPicker/teacherPicker.helper';
import {
  asObjectId, bySchoolScore, chosenIds, coordinatesOf, countAssignedTeachers, findInstitutions, idsOf, initialsOf, InstitutionPickerWho, namesOf,
  SchoolRow, TEACHER_FIELDS, toSchoolRow,
} from './institutionPicker.helper';

type Chip = { id: string; name: string; initials: string; isNew: boolean };
type Details = { courses: number | null; tags: Array<{ name: string; color?: string }> };

const DEBOUNCE_MS = 300;
const DETAIL_COUNT = 10;

/**
 * Ranked school suggestions for a teacher / applicant (chosen in the application, availability overlap, air-line distance, demand)
 * plus a free name search. Only edits the selection (v-model with institution ids), saving is done by the surrounding dialog.
 */
export default Vue.extend({
  components: {SegmentedControl},
  props: {
    mode: {type: String as () => PickerMode, required: true},
    employeeId: {type: Object as () => Types.ObjectId, required: true},
    /** Currently selected institution ids */
    value: {type: Array as () => string[], required: true},
    /** Institution ids selected when the dialog was opened */
    initialIds: {type: Array as () => string[], required: true},
    /** Names already known from the caller (id → name) */
    knownNames: {type: Object as () => Record<string, string>, default: () => ({})},
    /** Loads the suggestions whenever this becomes true */
    active: {type: Boolean, default: true},
  },
  data: () => ({
    loading: false,
    searchLoading: false,
    teacher: null as Employee | null,
    origin: null as number[] | null,
    nearby: [] as Institution[],
    results: [] as Institution[],
    term: '' as string | null,
    timer: undefined as number | undefined,
    run: 0,
    searchRun: 0,
    radius: 20,
    who: 'all' as InstitutionPickerWho,
    onlyMatching: false,
    limit: 20,
    teachers: {} as Record<string, number | null>,
    details: {} as Record<string, Details>,
    names: {} as Record<string, string>,
  }),
  computed: {
    radiusSteps: () => RADIUS_STEPS,
    workDays: () => WORK_DAYS,
    chosen(): string[] {
      return chosenIds(this.teacher);
    },
    canCount(): boolean {
      return this.$$crudAccessManager.canReadAllProvided(CrudEntityTypes.EMPLOYEE, EmployeeFields.ASSIGNED_INSTITUTIONS);
    },
    whoItems(): Array<{ value: InstitutionPickerWho; text: string }> {
      const items: Array<{ value: InstitutionPickerWho; text: string }> = [{value: 'all', text: 'Alle'}];
      if (this.canCount) {
        items.push({value: 'free', text: 'Ohne Lehrkraft'});
      }
      return this.chosen.length ? [...items, {value: 'chosen', text: 'Von Bewerber:in gewählt'}] : items;
    },
    searching(): boolean {
      return (this.term || '').trim().length >= 2;
    },
    teacherHasSlots(): boolean {
      return courseSlots(this.teacher?.availabilities).length > 0;
    },
    candidates(): SchoolRow[] {
      const source = this.searching ? this.results : this.nearby;
      return source.map((institution) => toSchoolRow(institution, this.teacher, this.mode, this.chosen, this.teachers[String(institution._id)]))
        .sort(bySchoolScore);
    },
    filtered(): SchoolRow[] {
      return this.candidates.filter((row) => (this.searching || !row.inactive || this.isSelected(row.id))
        && (this.searching || this.who === 'chosen' || row.pinned || row.distanceKm === null
          || row.distanceKm <= this.radius || this.isSelected(row.id))
        && (this.who !== 'free' || this.teachers[row.id] === 0)
        && (this.who !== 'chosen' || row.chosen)
        && (!this.onlyMatching || row.matchDays.length > 0));
    },
    shown(): SchoolRow[] {
      return this.filtered.slice(0, this.limit);
    },
    selectedChips(): Chip[] {
      return this.value.map((id) => this.chip(id));
    },
    removedChips(): Chip[] {
      return this.initialIds.filter((id) => !this.value.includes(id)).map((id) => this.chip(id));
    },
    hint(): string {
      if (this.searching) {
        return `Suchergebnisse für „${(this.term || '').trim()}“, unabhängig vom Umkreis (auch inaktive Standorte)`;
      }
      if (!this.origin) {
        return 'Die Lehrkraft hat keine Adresse mit Koordinaten, darum gibt es keine Vorschläge nach Entfernung. Bitte per Name suchen.';
      }
      const parts = [this.chosen.length ? 'Wahl in der Bewerbung' : '', this.teacherHasSlots ? 'passende Kurszeiten' : '', 'Entfernung', 'Bedarf']
        .filter(Boolean).join(', ');
      const noSlots = this.teacherHasSlots ? '' : ' · Lehrkraft hat keine Verfügbarkeit hinterlegt';
      return `${this.filtered.length} aktive Standorte im Umkreis von ${this.radius} km, sortiert nach ${parts}${noSlots}`;
    },
    emptyTitle(): string {
      return this.searching ? 'Kein Standort gefunden' : 'Keine Vorschläge für diese Filter';
    },
    emptyText(): string {
      if (this.searching) {
        return 'Anderen Namen oder die Standortnummer versuchen.';
      }
      return this.radius < MAX_RADIUS_KM ? 'Umkreis vergrößern, Filter lockern oder oben per Name suchen.' : 'Filter lockern oder oben per Name suchen.';
    },
    detailIds(): string {
      return this.shown.slice(0, DETAIL_COUNT).map((row) => row.id).filter((id) => !this.details[id]).join(',');
    },
    countIds(): string {
      return this.candidates.map((row) => row.id).filter((id) => !(id in this.teachers)).join(',');
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(open: boolean) {
        if (open) {
          this.who = 'all';
          this.term = '';
          this.onlyMatching = false;
          this.limit = 20;
          this.load();
        }
      },
    },
    term(value: string | null) {
      window.clearTimeout(this.timer);
      this.limit = 20;
      if (!value || value.trim().length < 2) {
        this.results = [];
        this.searchLoading = false;
        return;
      }
      this.searchLoading = true;
      this.timer = window.setTimeout(() => this.search(value.trim()), DEBOUNCE_MS);
    },
    radius() {
      this.limit = 20;
    },
    who() {
      this.limit = 20;
    },
    detailIds(ids: string) {
      if (ids) {
        this.loadDetails(ids.split(','));
      }
    },
    countIds(ids: string) {
      if (ids && !this.loading) {
        this.loadCounts(ids.split(','));
      }
    },
  },
  beforeDestroy() {
    window.clearTimeout(this.timer);
  },
  methods: {
    formatKm,
    isSelected(id: string): boolean {
      return this.value.includes(id);
    },
    toggle(id: string) {
      this.$emit('input', this.isSelected(id) ? this.value.filter((el) => el !== id) : [...this.value, id]);
    },
    toggleRow(row: SchoolRow) {
      this.$set(this.names, row.id, row.name);
      this.toggle(row.id);
    },
    chip(id: string): Chip {
      const name = this.names[id] || (this.knownNames as Record<string, string>)[id] || 'Unbekannter Standort';
      return {id, name, initials: initialsOf(name), isNew: !this.initialIds.includes(id)};
    },
    teacherCount(row: SchoolRow): number | null {
      return this.teachers[row.id] ?? null;
    },
    courseCount(row: SchoolRow): number | null {
      return this.details[row.id]?.courses ?? null;
    },
    tagsOf(row: SchoolRow): Array<{ name: string; color?: string }> {
      return (this.details[row.id]?.tags || []).slice(0, 4);
    },
    dayTitle(row: SchoolRow): string {
      if (!row.days.some((state) => state === 'match' || state === 'available')) {
        return 'Die Schule hat keine Kurszeiten hinterlegt (Rahmen = Lehrkraft verfügbar)';
      }
      return row.matchDays.length ? 'Grün: passt zur Verfügbarkeit der Lehrkraft · Blau: Kurszeit der Schule · Rahmen: Lehrkraft verfügbar, Schule nicht'
        : 'Keine Überschneidung mit der Verfügbarkeit der Lehrkraft (Blau = Kurszeit der Schule)';
    },
    async loadTeacher() {
      const entity = this.$$crudAccessManager.entity(CrudEntityTypes.EMPLOYEE, this.employeeId);
      const fields = TEACHER_FIELDS.filter((field) => (field !== EmployeeFields.ADDRESS || entity.canRead(EmployeeFields.ADDRESS))
        && (field !== EmployeeFields.VANILLA_APPLICATION || entity.canRead(EmployeeFields.VANILLA_APPLICATION)));
      const teacher = await EmployeeHandlerAPI.findById(this.employeeId, {
        fields,
        populate: {assignedInstitutions: {fields: ['name']}, plannedInstitutions: {fields: ['name']}},
      } as any);
      this.teacher = teacher;
      this.names = {...this.names, ...namesOf(teacher.assignedInstitutions as unknown[]), ...namesOf(teacher.plannedInstitutions as unknown[])};
      this.$emit('teacher', teacher);
      this.origin = coordinatesOf(teacher);
    },
    /** Chosen and (assign mode) planned schools are always offered, even if they are farther away or inactive */
    pinnedIds(): string[] {
      const planned = this.mode === 'assign' ? idsOf(this.teacher?.plannedInstitutions as unknown[] | undefined) : [];
      return Array.from(new Set([...this.chosen, ...planned]));
    },
    async load() {
      const run = ++this.run;
      this.loading = true;
      this.nearby = [];
      this.teachers = {};
      this.details = {};
      try {
        await this.loadTeacher();
        const near = this.origin
          ? findInstitutions({near: {lat: this.origin[1], lon: this.origin[0], maxDistance: MAX_RADIUS_KM * 1000}, isActive: true}, {skipPagination: true})
          : Promise.resolve([] as Institution[]);
        const pinned = this.pinnedIds();
        const extra = pinned.length ? findInstitutions({_id: pinned.map((id) => asObjectId(id))}, {skipPagination: true}).catch(() => [] as Institution[])
          : Promise.resolve([] as Institution[]);
        const [nearby, pinnedSchools] = await Promise.all([near, extra]);
        const seen = new Set(nearby.map((el) => String(el._id)));
        const all = [...nearby, ...pinnedSchools.filter((el) => !seen.has(String(el._id)))];
        if (run === this.run) {
          await this.loadCounts(all.map((el) => String(el._id)));
          this.nearby = all;
        }
      } catch (e) {
        this.$$showSnackbar('Beim Laden der Vorschläge ist ein Fehler aufgetreten', 'error');
      } finally {
        if (run === this.run) {
          this.loading = false;
        }
      }
    },
    async search(term: string) {
      const run = ++this.searchRun;
      try {
        const schools = await findInstitutions({$search: term}, {limit: 10});
        if (run === this.searchRun) {
          this.results = schools;
        }
      } catch (e) {
        if (run === this.searchRun) {
          this.results = [];
        }
      } finally {
        if (run === this.searchRun) {
          this.searchLoading = false;
        }
      }
    },
    /** Number of other active teachers per school; needed for ranking and the "Ohne Lehrkraft" filter */
    async loadCounts(ids: string[]) {
      if (!ids.length || !this.canCount) {
        return;
      }
      ids.forEach((id) => this.$set(this.teachers, id, null));
      const counts = await countAssignedTeachers(ids, String(this.employeeId));
      this.teachers = {...this.teachers, ...counts};
    },
    /** Active courses and tags only for the first rows, so opening the dialog stays cheap */
    async loadDetails(ids: string[]) {
      ids.forEach((id) => this.$set(this.details, id, {courses: null, tags: []}));
      const objectIds = ids.map((id) => asObjectId(id));
      const tags = InstitutionerApi.find({
        filter: {_id: objectIds},
        fields: ['tags'],
        populate: {tags: {fields: ['name', 'color']}},
        skipPagination: true,
      } as any).then((resp) => resp.institutions.forEach((institution) => {
        const list = ((institution.tags || []) as unknown[]).filter((el) => (el as { name?: string })?.name) as Array<{ name: string; color?: string }>;
        const id = String(institution._id);
        this.$set(this.details, id, {...this.details[id], tags: list});
      })).catch(() => undefined);
      const courses = objectIds.map((id) => CourseManagerAPI.find({
        filter: {institution: [id], courseState: [CourseStates.ACTIVE], endsAfter: new Date()},
        limit: 1,
        fields: ['_id'],
      } as any).then((resp) => {
        this.$set(this.details, String(id), {...this.details[String(id)], courses: resp.maxPage || 0});
      }).catch(() => undefined));
      await Promise.all([tags, ...courses]);
    },
  },
});
</script>

<style scoped src="@/components/institutions/institutionView/profileView/teacherPicker/teacherPicker.css"></style>
<style scoped src="./institutionPicker.css"></style>
<style src="@/components/common/modern/modern.css"></style>
<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
